import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import {
  FileText,
  Search,
  Filter,
  Calendar,
  User,
  Settings,
  Shield,
  CreditCard,
  Users,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const EVENT_TYPES = {
  membership_change: { label: 'Membership Change', icon: CreditCard, color: 'text-amber-600' },
  permission_change: { label: 'Permission Change', icon: Shield, color: 'text-indigo-600' },
  user_action: { label: 'User Action', icon: User, color: 'text-blue-600' },
  settings_change: { label: 'Settings Change', icon: Settings, color: 'text-slate-600' },
  import_action: { label: 'Import', icon: RefreshCw, color: 'text-emerald-600' },
  staff_change: { label: 'Staff Change', icon: Users, color: 'text-purple-600' },
};

/**
 * Audit Logs Section
 * 
 * View agency-level audit events and activity history.
 */
const AuditLogsSection = ({ agency, agencyId, isReadOnly }) => {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [eventFilter, setEventFilter] = useState('all');
  const [page, setPage] = useState(1);
  
  useEffect(() => {
    const fetchLogs = async () => {
      try {
        // In real implementation, this would fetch from the API
        // For now, use sample data
        setLogs([
          {
            id: 1,
            event_type: 'membership_change',
            action: 'Plan upgraded to Professional',
            actor: 'Tom Admin',
            actor_email: 'info@refug4biz.com',
            timestamp: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
            details: { old_plan: 'Basic', new_plan: 'Professional' },
          },
          {
            id: 2,
            event_type: 'permission_change',
            action: 'User permissions updated',
            actor: 'Tom Admin',
            actor_email: 'info@refug4biz.com',
            target_user: 'john@agency.com',
            timestamp: new Date(Date.now() - 1000 * 60 * 120).toISOString(),
            details: { permissions: ['can_view_reports', 'can_edit_cases'] },
          },
          {
            id: 3,
            event_type: 'user_action',
            action: 'New user invited',
            actor: 'Tom Admin',
            actor_email: 'info@refug4biz.com',
            target_user: 'newuser@agency.com',
            timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
            details: { role: 'staff' },
          },
          {
            id: 4,
            event_type: 'settings_change',
            action: 'Portal access configuration updated',
            actor: 'Support Admin',
            actor_email: 'support@fundelite.com',
            timestamp: new Date(Date.now() - 1000 * 60 * 60 * 48).toISOString(),
            details: { section: 'credit_builder', old_access: 'hidden', new_access: 'full_access' },
            is_support_action: true,
          },
          {
            id: 5,
            event_type: 'import_action',
            action: 'CSV users imported',
            actor: 'Tom Admin',
            actor_email: 'info@refug4biz.com',
            timestamp: new Date(Date.now() - 1000 * 60 * 60 * 72).toISOString(),
            details: { users_imported: 5, users_skipped: 1 },
          },
        ]);
      } catch (err) {
        console.error('Failed to fetch audit logs:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchLogs();
  }, [token, agencyId, page]);
  
  // Filter logs
  const filteredLogs = logs.filter(log => {
    if (eventFilter !== 'all' && log.event_type !== eventFilter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        log.action.toLowerCase().includes(query) ||
        log.actor?.toLowerCase().includes(query) ||
        log.actor_email?.toLowerCase().includes(query)
      );
    }
    return true;
  });
  
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };
  
  return (
    <div className="space-y-6" data-testid="audit-logs-section">
      <div className="border-b border-slate-200 pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
            <FileText className="text-slate-600" size={20} />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Audit Logs</h2>
            <p className="text-sm text-slate-500">
              View agency activity and change history
            </p>
          </div>
        </div>
      </div>
      
      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search logs..."
            className="pl-10 bg-white"
          />
        </div>
        
        <Select value={eventFilter} onValueChange={setEventFilter}>
          <SelectTrigger className="w-48 bg-white">
            <Filter size={14} className="mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All Events</SelectItem>
            {Object.entries(EVENT_TYPES).map(([key, { label }]) => (
              <SelectItem key={key} value={key}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      {/* Logs List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full"></div>
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            No audit logs found
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filteredLogs.map((log) => {
              const eventType = EVENT_TYPES[log.event_type] || EVENT_TYPES.user_action;
              const Icon = eventType.icon;
              
              return (
                <div key={log.id} className="p-4 hover:bg-slate-50">
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center ${eventType.color}`}>
                      <Icon size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-900">{log.action}</p>
                        {log.is_support_action && (
                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">
                            Support Action
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                        <User size={14} />
                        <span>{log.actor}</span>
                        <span>•</span>
                        <span className="text-slate-400">{log.actor_email}</span>
                      </div>
                      {log.target_user && (
                        <p className="text-sm text-slate-400 mt-1">
                          Target: {log.target_user}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-500">{formatTimestamp(log.timestamp)}</p>
                      <Badge variant="outline" className="mt-1 text-xs">
                        {eventType.label}
                      </Badge>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          Showing {filteredLogs.length} of {logs.length} events
        </p>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
            <ChevronLeft size={14} className="mr-1" />
            Previous
          </Button>
          <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)}>
            Next
            <ChevronRight size={14} className="ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AuditLogsSection;
